import React, { useState, useEffect } from "react";
import { useServerApi } from "../hooks/userServerApi";
import UsersAvatar from "./usersAvatar";
import { IconCirclePlus, IconCircleCheck, IconExclamationMark, IconDotsVertical } from "@tabler/icons";
import { useTranslation } from "react-i18next";
import { useTabNavigate } from "../hooks/useTabNavigate";
import { subscribeListRefresh } from "../context/tabContext";
import { showNotification } from "@mantine/notifications";

import {
  Paper,
  Group,
  Title,
  Text,
  Button,
  Stack,
  SimpleGrid,
  Menu,
  ActionIcon,
} from "@mantine/core";

const UserRoleCard = ({ role, handleEditUserRole, handleDeleteUserRole }) => {
  const { t } = useTranslation();
  return (
    <Paper radius="md" p="md" key={role._id} shadow="md" style={{ minHeight: 40 }}>
      <Stack align="flex-start" justify="space-between">
        <Group position="apart" style={{ width: "100%" }}>
          <div>
            <Text color={"dimmed"} size="xs">
              {t("Total ")} {role.count} {t("users")}
            </Text>
            <Text weight={500} size="md">
              {role.name} {role.cname}
            </Text>
          </div>
          <UsersAvatar users={role.users} maxDisplayUser={4} size={35} />
        </Group>

        <Group position="apart" style={{ width: "100%" }}>
          <Button
            size="xs"
            variant="subtle"
            p={0}
            onClick={() => handleEditUserRole(role._id)}
          >
            {t("Edit Role")}
          </Button>
          <Menu shadow="md" withinPortal position="bottom-end">
            <Menu.Target>
              <ActionIcon variant="subtle" size="sm" aria-label={t("Role actions")}>
                <IconDotsVertical size={16} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item color="red" onClick={() => handleDeleteUserRole(role)}>
                {t("Delete Role")}
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Stack>
    </Paper>
  );
};

const RoleList = ({ roles, width, handleEditUserRole, handleDeleteUserRole }) => {
  return (
    <SimpleGrid
      cols={4}
      spacing="lg"
      breakpoints={[
        { maxWidth: "md", cols: 3, spacing: "md" },
        { maxWidth: "sm", cols: 2, spacing: "sm" },
        { maxWidth: "xs", cols: 1, spacing: "sm" },
      ]}
    >
      {roles.map((role) => (
        <UserRoleCard
          key={role._id}
          role={role}
          handleEditUserRole={handleEditUserRole}
          handleDeleteUserRole={handleDeleteUserRole}
        />
      ))}
    </SimpleGrid>
  );
};

const fectchAllUserRoles = async (api) => {
  let result = await api.search({
    apiEntity: "userRole",
    pageSize: 100,
    currentPage: 1,
    sort: { by: "name", order: "asc" },
  });
  return result?.docs ?? [];
};

const ROLE_EDIT_PATH = "/operation/userRole/edit";

const buildRoleEditSearch = (params, returnPath) => {
  const qs = new URLSearchParams(params);
  if (returnPath) qs.set("returnTo", returnPath);
  return `?${qs.toString()}`;
};

const UserRoleList = ({
  width = 300,
  title,
  returnPath = "/operation/user",
  ...props
}) => {
  const [api] = useServerApi();
  const [roles, setRoles] = useState([]);
  const tabNav = useTabNavigate();
  const { t } = useTranslation();
  const description = t(
    "A role provide access to predefined menus and features so that depending on assigned role an administrator can have access to what he need"
  );

  const fetchData = async () => {
    try {
      let userRoles = await fectchAllUserRoles(api);
      let data = await api.User.getListGroupByRole();
      userRoles.forEach((role) => {
        let r = data.find((dr) => dr._id === role._id);
        role.count = r ? r.count : 0;
        role.users = r ? r.users : [];
      });
      setRoles(userRoles);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    return subscribeListRefresh(returnPath, fetchData);
  }, [returnPath]);

  const handleAddUser = () => {
    tabNav.open({
      pathname: ROLE_EDIT_PATH,
      search: buildRoleEditSearch({ mode: "add" }, returnPath),
      label: "New User Role",
    });
  };

  const handleEditUserRole = (id) => {
    tabNav.open({
      pathname: ROLE_EDIT_PATH,
      search: buildRoleEditSearch({ id, mode: "edit" }, returnPath),
      label: "User Role",
    });
  };

  const handleDeleteUserRole = async (role) => {
    if (role.count > 0) {
      showNotification({
        title: t("Delete Fail"),
        message: t(
          "This role has {{count}} user(s). Remove or reassign users before deleting.",
          { count: role.count }
        ),
        color: "red",
        icon: <IconExclamationMark size={18} />,
      });
      return;
    }

    const roleLabel = [role.name, role.cname].filter(Boolean).join(" ");
    if (
      !window.confirm(
        t("Are you sure you want to delete role \"{{name}}\"?", {
          name: roleLabel,
        })
      )
    ) {
      return;
    }

    const result = await api.removeById({ apiEntity: "userRole", id: role._id });
    if (!result?.success) return;

    showNotification({
      title: t("Delete Success"),
      message: t("Role deleted successfully."),
      color: "green",
      icon: <IconCircleCheck size={18} />,
    });
    fetchData();
  };

  return (
    <div style={{ padding: 20 }}>
      <div style={{ marginBottom: 20 }}>
        {title ? <Title order={2}>{title}</Title> : null}
        <Text color={"dimmed"} size="xs">
          {t(description)}
        </Text>
      </div>

      {roles && (
        <RoleList
          roles={roles}
          width={width}
          handleEditUserRole={handleEditUserRole}
          handleDeleteUserRole={handleDeleteUserRole}
        />
      )}

      <Button size="xs" variant="subtle" onClick={handleAddUser} mt="xl">
        <IconCirclePlus size={15} />
        {t("New User Role")}
      </Button>
    </div>
  );
};

export default UserRoleList;
